import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Dashboard.css';
import { useNavigate } from 'react-router-dom';

// ===== AUDIO ENGINE =====
const AudioEngine = (() => {
  let ctx = null;

  const initCtx = () => {
    if (!ctx) {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
    return ctx;
  };

  const warmUp = () => {
    const ac = initCtx();
    const buf = ac.createBuffer(1, 1, 22050);
    const src = ac.createBufferSource();
    src.buffer = buf;
    src.connect(ac.destination);
    src.start(0);
  };

  document.addEventListener('click', warmUp, { once: true });
  document.addEventListener('touchstart', warmUp, { once: true });

  const play = (type) => {
    try {
      const ac = initCtx();
      const o = ac.createOscillator();
      const g = ac.createGain();
      o.connect(g);
      g.connect(ac.destination);

      const sounds = {
        click:    { freq: [523, 659],         dur: 0.08, wave: 'square',   vol: 0.12 },
        navigate: { freq: [440, 523],         dur: 0.07, wave: 'square',   vol: 0.10 },
        expand:   { freq: [330, 440],         dur: 0.07, wave: 'triangle', vol: 0.10 },
        collapse: { freq: [440, 330],         dur: 0.07, wave: 'triangle', vol: 0.08 },
        tab:      { freq: [600, 750],         dur: 0.06, wave: 'sine',     vol: 0.10 },
        refresh:  { freq: [440, 523, 659],    dur: 0.07, wave: 'sine',     vol: 0.12 },
        hover:    { freq: [700],              dur: 0.04, wave: 'sine',     vol: 0.05 },
        toast:    { freq: [523, 659, 784],    dur: 0.07, wave: 'sine',     vol: 0.12 },
        action:   { freq: [392, 523, 659],    dur: 0.08, wave: 'square',   vol: 0.15 },
      };

      const s = sounds[type] || sounds.click;
      const freqs = Array.isArray(s.freq) ? s.freq : [s.freq];
      o.type = s.wave;
      g.gain.setValueAtTime(s.vol, ac.currentTime);

      freqs.forEach((f, i) => {
        o.frequency.setValueAtTime(f, ac.currentTime + i * s.dur);
      });

      const totalDur = freqs.length * s.dur;
      g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + totalDur + 0.05);
      o.start(ac.currentTime);
      o.stop(ac.currentTime + totalDur + 0.1);
    } catch (e) {}
  };

  return { play };
})();

// ===== ANIMATION HOOKS =====
const useCountUp = (target, duration = 1200, start = false) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start || target === 0) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return value;
};

const useInView = (ref) => {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return inView;
};

// ===== ANIMATED BAR =====
const AnimatedBar = ({ width, color, delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref);
  return (
    <div ref={ref} className="db-bar-track">
      <div
        className="db-bar-fill"
        style={{
          width: inView ? `${Math.max(width, 1)}%` : '0%',
          background: color,
          transitionDelay: `${delay}ms`
        }}
      />
    </div>
  );
};

// ===== STAT CARD =====
const StatCard = ({ icon, label, value, suffix, color, bg, progress, index }) => {
  const ref = useRef(null);
  const inView = useInView(ref);
  const count = useCountUp(value, 1200, inView);
  return (
    <div ref={ref} className="db-stat-card" style={{ '--c': color, '--bg': bg, animationDelay: `${index * 80}ms` }}>
      <div className="db-stat-icon-box" style={{ background: bg }}>
        <span>{icon}</span>
      </div>
      <div className="db-stat-body">
        <p className="db-stat-label">{label}</p>
        <p className="db-stat-value" style={{ color }}>{count}{suffix}</p>
        <div className="db-stat-prog-track">
          <div
            className="db-stat-prog-fill"
            style={{
              width: inView ? `${Math.min(progress, 100)}%` : '0%',
              background: color
            }}
          />
        </div>
      </div>
    </div>
  );
};

// ===== MAIN DASHBOARD =====
const Dashboard = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState({ name: 'Student', grade: '5', avatar_emoji: '👤' });
  const [metrics, setMetrics] = useState({ accuracy: 0, avg_time: 0, mastery_level: 0, streak: 0 });
  const [concepts, setConcepts] = useState([]);
  const [graphData, setGraphData] = useState({ concepts: [] });
  const [recommendations, setRecommendations] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeGraphView, setActiveGraphView] = useState('mastery');
  const [expandedConcept, setExpandedConcept] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    AudioEngine.play('toast');
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ===== API CALLS =====
  const loadUserData = useCallback(async () => {
    try {
      const res = await fetch('/api/profile');
      const data = await res.json();
      if (data.success && data.user) {
        setUser({ name: data.user.name, grade: data.user.grade, avatar_emoji: data.user.avatar_emoji });
      }
    } catch (e) {}
  }, []);

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const metricsRes = await fetch('/api/student-success-metrics');
      const metricsData = await metricsRes.json();
      if (metricsData.success) setMetrics(metricsData.metrics);
      await loadParentConcepts();
      await loadGraphData();
      await loadRecommendations();
    } catch (e) {
      showToast('Connection issue. Please refresh.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadParentConcepts = async () => {
    try {
      const res = await fetch('/api/hierarchical-concept-progress');
      const data = await res.json();
      if (data.success) {
        setConcepts(data.concepts);
        updateOverallMetrics(data.concepts);
      }
    } catch (e) {}
  };

  const loadGraphData = async () => {
    try {
      const res = await fetch('/api/hierarchical-concept-progress');
      const data = await res.json();
      if (data.success && data.concepts) {
        const transformed = data.concepts.map(concept => {
          const sel = concept.subconcepts?.filter(sc => sc.is_selected) || [];
          const avgMastery = sel.reduce((s, sc) => s + (sc.mastery_level || 0), 0) / sel.length || 0;
          const avgAccuracy = sel.reduce((s, sc) => s + (sc.accuracy || 0), 0) / sel.length || 0;
          const totalAttempts = sel.reduce((s, sc) => s + (sc.total_attempts || 0), 0);
          return {
            concept_name: concept.concept_name,
            mastery: concept.overall_progress || avgMastery,
            assessment_mastery: avgAccuracy,
            practice_mastery: avgMastery,
            total_attempts: totalAttempts,
            needs_practice: concept.needs_practice || false
          };
        }).filter(c => c.mastery > 0);
        setGraphData({ concepts: transformed });
      }
    } catch (e) {}
  };

  const loadRecommendations = async () => {
    try {
      const res = await fetch('/api/priority-practice-concepts');
      const data = await res.json();
      setRecommendations(data.success && data.priority_concepts.length > 0 ? data.priority_concepts.slice(0, 3) : []);
    } catch (e) { setRecommendations([]); }
  };

  const loadInsights = useCallback(() => {
    const list = graphData.concepts;
    if (!list.length) { setInsights([]); return; }
    const mastered = list.filter(c => c.mastery >= 80).length;
    const needsWork = list.filter(c => c.mastery < 50 && c.mastery > 0).length;
    const avg = list.reduce((s, c) => s + c.mastery, 0) / list.length;
    const gap = list.reduce((s, c) => s + Math.max(0, (c.assessment_mastery || 0) - (c.practice_mastery || 0)), 0) / list.length;
    const out = [];
    if (mastered > list.length * 0.3) out.push({ type: 'positive', title: 'Strong Performance', content: `${mastered} topics have been mastered — excellent progress!` });
    if (needsWork > list.length * 0.4) out.push({ type: 'warning', title: 'Needs Attention', content: `${needsWork} topics need more practice time.` });
    if (gap > 15) out.push({ type: 'info', title: 'Practice Gap Detected', content: `A ${gap.toFixed(1)}% gap between accuracy and mastery — more practice recommended.` });
    out.push({ type: 'info', title: 'Overall Progress', content: `Current average mastery is ${avg.toFixed(1)}%. Focus on topics below 50% for fastest growth.` });
    setInsights(out);
  }, [graphData.concepts]);

  const updateOverallMetrics = (data) => {
    if (!data?.length) return;
    const avg = data.reduce((s, c) => s + (c.overall_progress || 0), 0) / data.length;
    setMetrics(prev => ({ ...prev, mastery_level: Math.round(avg) }));
  };

  const refreshProgress = async () => {
    AudioEngine.play('refresh');
    await loadParentConcepts();
    showToast('Progress refreshed successfully.', 'success');
  };

  useEffect(() => {
    setMounted(true);
    loadUserData();
    loadDashboardData();
  }, []);

  useEffect(() => {
    if (graphData.concepts.length > 0) loadInsights();
  }, [graphData.concepts]);

  const getMasteryTier = (m) => {
    if (m >= 80) return { label: 'Mastered',   color: '#059669', bg: '#ECFDF5', border: '#A7F3D0' };
    if (m >= 60) return { label: 'Proficient',  color: '#2563EB', bg: '#EFF6FF', border: '#BFDBFE' };
    if (m >= 40) return { label: 'Developing',  color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' };
    return          { label: 'Beginner',    color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' };
  };

  const getBarColor = (m) => {
    if (m >= 80) return '#059669';
    if (m >= 60) return '#2563EB';
    if (m >= 40) return '#D97706';
    return '#DC2626';
  };

  const avgPower = graphData.concepts.length > 0
    ? (graphData.concepts.reduce((s, c) => s + c.mastery, 0) / graphData.concepts.length).toFixed(1)
    : '0';

  // ===== Tab switch handler =====
  const handleTabSwitch = (key) => {
    AudioEngine.play('tab');
    setActiveGraphView(key);
  };

  // ===== Concept expand/collapse =====
  const handleConceptToggle = (i, isExpanded) => {
    AudioEngine.play(isExpanded ? 'collapse' : 'expand');
    setExpandedConcept(isExpanded ? null : i);
  };

  // ===== Navigate handlers =====
  const handleNavigateStudentView = () => {
    AudioEngine.play('navigate');
    navigate('/dashboard');
  };

  const handleManageTopics = () => {
    AudioEngine.play('action');
    window.location.href = '/parent-manage-concepts';
  };

  const handleStartPractice = (rec) => {
    AudioEngine.play('action');
    window.location.href = `/parent-prepare-assessment?type=practice&concept=${encodeURIComponent(rec.concept)}`;
  };

  if (loading) {
    return (
      <div className="db-loading">
        <div className="db-loading-card">
          <div className="db-spinner" />
          <p className="db-loading-text">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`db-root ${mounted ? 'db-mounted' : ''}`}>

      {/* Toast */}
      {toast && (
        <div className={`db-toast db-toast--${toast.type}`}>
          <span className="db-toast-dot" />
          {toast.message}
        </div>
      )}

      {/* ===== TOP NAV ===== */}
      <header className="db-nav">
        <div className="db-nav-inner">
          <div className="db-nav-brand">
            <div className="db-brand-mark">📚</div>
            <div>
              <div className="db-brand-name">SolveSmart</div>
              <div className="db-brand-sub">Parent Dashboard</div>
            </div>
          </div>
          <nav className="db-nav-links">
            <button
              className="db-nav-btn"
              onClick={handleNavigateStudentView}
              onMouseEnter={() => AudioEngine.play('hover')}
            >
              Switch to Student View
            </button>
            <button
              className="db-nav-btn db-nav-btn--primary"
              onClick={handleManageTopics}
              onMouseEnter={() => AudioEngine.play('hover')}
            >
              Manage Topics
            </button>
          </nav>
        </div>
      </header>

      <div className="db-body">

        {/* ===== HERO ===== */}
        <section className="db-hero">
          <div className="db-hero-content">
            <div className="db-hero-text">
              <div className="db-hero-eyebrow">Parent Overview</div>
              <h1 className="db-hero-title">
                Welcome back, <span className="db-hero-accent">Parent</span>
              </h1>
              <p className="db-hero-desc">
                Monitoring <strong>{user.name}</strong>'s learning journey — Grade {user.grade}
              </p>
              <div className="db-hero-pills">
                <span className="db-pill db-pill--green">
                  <span className="db-pill-dot" style={{ background: '#059669' }} />
                  Mastery {metrics.mastery_level}%
                </span>
                <span className="db-pill db-pill--amber">
                  <span className="db-pill-dot" style={{ background: '#D97706' }} />
                  {metrics.streak}-day streak
                </span>
                <span className="db-pill db-pill--blue">
                  <span className="db-pill-dot" style={{ background: '#2563EB' }} />
                  {Math.round(metrics.accuracy)}% accuracy
                </span>
              </div>
            </div>
            <div className="db-hero-avatar">
              <div className="db-avatar-ring" />
              <div className="db-avatar-circle">
                <span>{user.avatar_emoji}</span>
              </div>
            </div>
          </div>

          {/* Quick action strip */}
          <div
            className="db-hero-action"
            onClick={handleManageTopics}
            onMouseEnter={() => AudioEngine.play('hover')}
          >
            <div className="db-hero-action-left">
              <span className="db-hero-action-icon">📋</span>
              <div>
                <div className="db-hero-action-title">Manage Learning Topics</div>
                <div className="db-hero-action-desc">Add or remove topics from your child's learning plan</div>
              </div>
            </div>
            <span className="db-hero-action-arrow">→</span>
          </div>
        </section>

        {/* ===== STAT CARDS ===== */}
        <section className="db-stats">
          <StatCard index={0} icon="🎯" label="Accuracy"               value={Math.round(metrics.accuracy)}      suffix="%" color="#2563EB" bg="#EFF6FF" progress={metrics.accuracy} />
          <StatCard index={1} icon="⏱️" label="Avg. Time per Question" value={Math.round(metrics.avg_time)}       suffix="s" color="#D97706" bg="#FFFBEB" progress={Math.min(100, (30 - metrics.avg_time) * 3.33)} />
          <StatCard index={2} icon="📈" label="Overall Mastery"         value={Math.round(metrics.mastery_level)} suffix="%" color="#059669" bg="#ECFDF5" progress={metrics.mastery_level} />
          <StatCard index={3} icon="🔥" label="Daily Streak"            value={metrics.streak}                    suffix=" days" color="#DC2626" bg="#FEF2F2" progress={Math.min(100, metrics.streak * 10)} />
        </section>

        {/* ===== TWO COLUMN LAYOUT ===== */}
        <div className="db-two-col">

          {/* LEFT — Analysis + Skill Tree */}
          <div className="db-col-main">

            {/* Battle Analysis */}
            <div className="db-card">
              <div className="db-card-header">
                <div>
                  <h2 className="db-card-title">Topic Performance</h2>
                  <p className="db-card-desc">Visual breakdown of mastery across topics</p>
                </div>
                <div className="db-tabs">
                  {[
                    { key: 'mastery',    label: 'Mastery Levels' },
                    { key: 'progress',   label: 'Timeline'       },
                    { key: 'comparison', label: 'Comparison'     }
                  ].map(t => (
                    <button
                      key={t.key}
                      className={`db-tab ${activeGraphView === t.key ? 'db-tab--active' : ''}`}
                      onClick={() => handleTabSwitch(t.key)}
                      onMouseEnter={() => AudioEngine.play('hover')}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Summary row */}
              <div className="db-summary-row">
                {[
                  { val: `${avgPower}%`,                                                                   lbl: 'Avg Mastery' },
                  { val: graphData.concepts.length,                                                        lbl: 'Topics'      },
                  { val: graphData.concepts.filter(c => c.mastery >= 80).length,                           lbl: 'Mastered'    },
                  { val: graphData.concepts.filter(c => c.mastery < 50 && c.mastery > 0).length,           lbl: 'Needs Work'  },
                ].map((s, i) => (
                  <div key={i} className="db-summary-chip">
                    <div className="db-summary-val">{s.val}</div>
                    <div className="db-summary-lbl">{s.lbl}</div>
                  </div>
                ))}
              </div>

              {/* Mastery View */}
              {activeGraphView === 'mastery' && (
                <div className="db-bars-list">
                  {graphData.concepts.length === 0 ? (
                    <div className="db-empty">No topic data yet. Add topics to get started.</div>
                  ) : graphData.concepts.map((c, i) => {
                    const color = getBarColor(c.mastery);
                    return (
                      <div key={i} className="db-bar-item" style={{ animationDelay: `${i * 50}ms` }}>
                        <div className="db-bar-row">
                          <span className="db-bar-name">{c.concept_name}</span>
                          <div className="db-bar-right">
                            {c.needs_practice && <span className="db-needs-badge">Needs Practice</span>}
                            <span className="db-bar-pct" style={{ color }}>{c.mastery.toFixed(1)}%</span>
                          </div>
                        </div>
                        <AnimatedBar width={c.mastery} color={color} delay={i * 50} />
                        <div className="db-bar-meta">
                          <span>Accuracy: {(c.assessment_mastery || 0).toFixed(1)}%</span>
                          <span>Practice Mastery: {(c.practice_mastery || 0).toFixed(1)}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Timeline View */}
              {activeGraphView === 'progress' && (
                <div className="db-timeline">
                  {['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Now'].map((w, i) => (
                    <div key={i} className="db-timeline-col">
                      <div className="db-timeline-bars">
                        <div className="db-tbar db-tbar--blue"  style={{ height: `${45 + i * 10}%` }}><span>{45 + i * 10}%</span></div>
                        <div className="db-tbar db-tbar--green" style={{ height: `${30 + i * 9}%`  }}><span>{30 + i * 9}%</span></div>
                      </div>
                      <div className="db-timeline-label">{w}</div>
                    </div>
                  ))}
                  <div className="db-timeline-legend">
                    <span><span className="db-legend-dot" style={{ background: '#2563EB' }} />Accuracy</span>
                    <span><span className="db-legend-dot" style={{ background: '#059669' }} />Mastery</span>
                  </div>
                </div>
              )}

              {/* Comparison View */}
              {activeGraphView === 'comparison' && (
                <div className="db-comparison">
                  {graphData.concepts.slice(0, 8).map((c, i) => (
                    <div key={i} className="db-cmp-col">
                      <div className="db-cmp-bars">
                        <div className="db-cbar db-cbar--blue"  style={{ height: `${c.assessment_mastery || 0}%` }} title={`Accuracy: ${c.assessment_mastery?.toFixed(1)}%`} />
                        <div className="db-cbar db-cbar--green" style={{ height: `${c.practice_mastery  || 0}%` }} title={`Mastery: ${c.practice_mastery?.toFixed(1)}%`} />
                      </div>
                      <div className="db-cmp-name">{c.concept_name.split(' ').slice(0, 2).join(' ')}</div>
                    </div>
                  ))}
                  <div className="db-timeline-legend">
                    <span><span className="db-legend-dot" style={{ background: '#2563EB' }} />Accuracy</span>
                    <span><span className="db-legend-dot" style={{ background: '#059669' }} />Mastery</span>
                  </div>
                </div>
              )}
            </div>

            {/* Skill Tree */}
            <div className="db-card">
              <div className="db-card-header">
                <div>
                  <h2 className="db-card-title">Skill Overview</h2>
                  <p className="db-card-desc">Detailed view of all topics and subtopics</p>
                </div>
                <button
                  className="db-outline-btn"
                  onClick={refreshProgress}
                  onMouseEnter={() => AudioEngine.play('hover')}
                >
                  ↻ Refresh
                </button>
              </div>

              {/* Overview tiles */}
              <div className="db-overview-tiles">
                {[
                  { icon: '📚', count: concepts.length,                                                                                              label: 'Total Topics', color: '#2563EB', bg: '#EFF6FF' },
                  { icon: '✅', count: concepts.filter(c => (c.overall_progress || 0) >= 80).length,                                                 label: 'Mastered',     color: '#059669', bg: '#ECFDF5' },
                  { icon: '📊', count: concepts.filter(c => (c.overall_progress || 0) >= 40 && (c.overall_progress || 0) < 80).length,               label: 'In Progress',  color: '#D97706', bg: '#FFFBEB' },
                  { icon: '⚠️', count: concepts.filter(c => (c.overall_progress || 0) > 0  && (c.overall_progress || 0) < 40).length,               label: 'Needs Work',   color: '#DC2626', bg: '#FEF2F2' },
                ].map((t, i) => (
                  <div key={i} className="db-overview-tile" style={{ '--tc': t.color, '--tbg': t.bg }}>
                    <div className="db-tile-icon" style={{ background: t.bg }}>{t.icon}</div>
                    <div className="db-tile-count" style={{ color: t.color }}>{t.count}</div>
                    <div className="db-tile-label">{t.label}</div>
                  </div>
                ))}
              </div>

              {/* Concept list */}
              <div className="db-concept-list">
                {concepts.map((concept, i) => {
                  const sel = concept.subconcepts?.filter(sc => sc.is_selected) || [];
                  if (!sel.length) return null;
                  const tier = getMasteryTier(concept.overall_progress || 0);
                  const isExpanded = expandedConcept === i;
                  return (
                    <div key={i} className={`db-concept-item ${isExpanded ? 'db-concept-item--open' : ''}`}>
                      <div
                        className="db-concept-header"
                        onClick={() => handleConceptToggle(i, isExpanded)}
                        onMouseEnter={() => AudioEngine.play('hover')}
                      >
                        <div className="db-concept-left">
                          <div className="db-concept-stripe" style={{ background: tier.color }} />
                          <div className="db-concept-icon" style={{ background: tier.bg }}>
                            {(concept.overall_progress || 0) >= 80 ? '✅'
                              : (concept.overall_progress || 0) >= 60 ? '📈'
                              : (concept.overall_progress || 0) >= 40 ? '📊'
                              : '📌'}
                          </div>
                          <div>
                            <div className="db-concept-name">{concept.concept_name}</div>
                            <div className="db-concept-meta">
                              {sel.length} subtopics · {concept.mastered_subconcepts || 0} mastered
                            </div>
                          </div>
                        </div>
                        <div className="db-concept-right">
                          <span
                            className="db-tier-badge"
                            style={{ background: tier.bg, color: tier.color, borderColor: tier.border }}
                          >
                            {Math.round(concept.overall_progress || 0)}%
                          </span>
                          <span className={`db-chevron ${isExpanded ? 'db-chevron--up' : ''}`}>▾</span>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="db-concept-body">
                          <div className="db-subtopic-mini-stats">
                            {[
                              { v: sel.length,                             l: 'Total',      c: '#2563EB' },
                              { v: concept.mastered_subconcepts || 0,     l: 'Mastered',   c: '#059669' },
                              { v: concept.needs_practice_subconcepts || 0, l: 'Needs Work', c: '#DC2626' },
                            ].map((s, j) => (
                              <div key={j} className="db-mini-stat">
                                <div className="db-mini-stat-val" style={{ color: s.c }}>{s.v}</div>
                                <div className="db-mini-stat-lbl">{s.l}</div>
                              </div>
                            ))}
                          </div>
                          <div className="db-subtopic-grid">
                            {sel.map((sub, j) => {
                              const st = getMasteryTier(sub.mastery_level || 0);
                              return (
                                <div key={j} className="db-subtopic-card" style={{ borderLeftColor: st.color }}>
                                  <div className="db-subtopic-top">
                                    <span className="db-subtopic-name">{sub.subconcept_name}</span>
                                    <span className="db-subtopic-pct" style={{ background: st.bg, color: st.color }}>
                                      {Math.round(sub.mastery_level || 0)}%
                                    </span>
                                  </div>
                                  <div className="db-subtopic-stats">
                                    <span>Mastery: {Math.round(sub.mastery_level || 0)}%</span>
                                    <span>Accuracy: {Math.round(sub.accuracy || 0)}%</span>
                                  </div>
                                  {sub.needs_practice && (
                                    <span className="db-subtopic-warn">Needs practice</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          {(concept.needs_practice_subconcepts || 0) > 0 && (
                            <div className="db-concept-notice">
                              {concept.needs_practice_subconcepts} subtopic(s) need additional practice to reach mastery.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {concepts.filter(c => c.subconcepts?.some(sc => sc.is_selected)).length === 0 && (
                  <div className="db-empty">No topics selected yet. Go to Manage Topics to get started.</div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT — Recommendations + Insights */}
          <div className="db-col-side">

            {/* Recommendations */}
            <div className="db-card">
              <div className="db-card-header db-card-header--stacked">
                <h2 className="db-card-title">Recommended Focus Areas</h2>
                <p className="db-card-desc">AI-suggested topics for priority practice</p>
              </div>
              <div className="db-recs-list">
                {recommendations.length > 0 ? recommendations.map((rec, i) => (
                  <div key={i} className="db-rec-item">
                    <div className="db-rec-header">
                      <div className="db-rec-icon">🎯</div>
                      <div>
                        <div className="db-rec-name">{rec.concept}</div>
                        {Math.abs((rec.assessment_mastery || 0) - (rec.practice_mastery || 0)) > 10 && (
                          <div className="db-rec-gap">Practice gap detected</div>
                        )}
                      </div>
                    </div>
                    <div className="db-rec-stats">
                      <div className="db-rec-stat">
                        <div className="db-rec-stat-val">{rec.practice_mastery?.toFixed(1)}%</div>
                        <div className="db-rec-stat-lbl">Mastery</div>
                      </div>
                      <div className="db-rec-stat">
                        <div className="db-rec-stat-val">{rec.assessment_mastery?.toFixed(1)}%</div>
                        <div className="db-rec-stat-lbl">Accuracy</div>
                      </div>
                    </div>
                    <button
                      className="db-train-btn"
                      onClick={() => handleStartPractice(rec)}
                      onMouseEnter={() => AudioEngine.play('hover')}
                    >
                      Start Practice Session →
                    </button>
                  </div>
                )) : (
                  <>
                    <div className="db-rec-info-card">
                      <div className="db-rec-info-title">📊 How Mastery Works</div>
                      <div className="db-rec-info-body">
                        <p>Accuracy reflects correctness in assessments. Mastery reflects deep understanding built through practice. Aim to keep both above 70%.</p>
                      </div>
                    </div>
                    <div className="db-rec-info-card">
                      <div className="db-rec-info-title">⏰ Best Practice Strategy</div>
                      <div className="db-rec-info-body">
                        <p>Focus on topics where mastery lags behind accuracy — that's where practice yields the fastest improvement.</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Insights */}
            <div className="db-card">
              <div className="db-card-header db-card-header--stacked">
                <h2 className="db-card-title">Insights & Analysis</h2>
                <p className="db-card-desc">Key observations from your child's performance</p>
              </div>
              <div className="db-insights-list">
                {insights.length > 0 ? insights.map((ins, i) => {
                  const styles = {
                    positive: { bg: '#ECFDF5', border: '#A7F3D0', color: '#065F46', icon: '✅' },
                    warning:  { bg: '#FEF2F2', border: '#FECACA', color: '#991B1B', icon: '⚠️' },
                    info:     { bg: '#EFF6FF', border: '#BFDBFE', color: '#1D4ED8', icon: '💡' },
                  };
                  const s = styles[ins.type] || styles.info;
                  return (
                    <div
                      key={i}
                      className="db-insight-card"
                      style={{ background: s.bg, borderColor: s.border }}
                    >
                      <span className="db-insight-icon">{s.icon}</span>
                      <div>
                        <div className="db-insight-title" style={{ color: s.color }}>{ins.title}</div>
                        <div className="db-insight-body">{ins.content}</div>
                      </div>
                    </div>
                  );
                }) : (
                  <div className="db-insight-card" style={{ background: '#EFF6FF', borderColor: '#BFDBFE' }}>
                    <span className="db-insight-icon">💡</span>
                    <div>
                      <div className="db-insight-title" style={{ color: '#1D4ED8' }}>Getting Started</div>
                      <div className="db-insight-body">Complete more learning activities to see personalized insights here.</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;